import { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import { 
  Home, Server, Users, Shield, LogOut,
  AlertCircle, CheckCircle, XCircle, AlertTriangle, X, MessageCircle
} from 'lucide-react';
import { api, BACKEND_URL } from './utils/api';
import { HomePage } from './HomePage';
import { ServersPage } from './ServersPage';
import { UsersPage } from './UsersPage';
import { GroupsPage } from './GroupsPage';
import { AuditLogPanel, FeatureTogglesPanel, BackupRestorePanel, NotificationsPanel, ThemeSettingsPanel } from './components/admin';
import { LoadingSpinner } from './components/common/Loading';
import { GroupContextProvider } from './contexts/GroupContext';

// Toast/Alert Notification Component with Lucide icons
const ToastContainer = ({ alerts, onDismiss }) => {
  if (!alerts || alerts.length === 0) return null;

  const getIcon = (type) => {
    switch (type) {
      case 'error': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-400" />;
      default: return <AlertCircle className="w-5 h-5 text-blue-400" />;
    }
  };

  const getBgClass = (type) => {
    switch (type) {
      case 'error': return 'bg-red-900/90 border-red-500';
      case 'warning': return 'bg-yellow-900/90 border-yellow-500';
      case 'success': return 'bg-green-900/90 border-green-500';
      default: return 'bg-blue-900/90 border-blue-500';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm" data-testid="server-alerts">
      {alerts.map((alert, index) => (
        <div
          key={alert.id || index}
          className={`p-4 rounded-lg shadow-lg border animate-slide-in ${getBgClass(alert.type)}`}
        >
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {getIcon(alert.type)}
                <span className="font-bold text-sm">{alert.title}</span>
              </div>
              <p className="text-sm text-gray-300">{alert.message}</p>
              {alert.server && (
                <p className="text-xs text-gray-400 mt-1">{alert.server}</p>
              )}
            </div>
            <button
              onClick={() => onDismiss(alert.id)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Admin Wizard Component
const AdminWizard = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [adminData, setAdminData] = useState({
    admin_username: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
  });
  const [orchData, setOrchData] = useState({
    orchestrator_name: '',
    orchestrator_url: '',
    orchestrator_api_key: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAdminSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (adminData.admin_password !== adminData.admin_password_confirm) {
      setError('Passwords do not match');
      return;
    }
    if (adminData.admin_password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setStep(2);
  };

  const handleSkipOrchestrator = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/system/wizard', {
        admin_username: adminData.admin_username,
        admin_email: adminData.admin_email,
        admin_password: adminData.admin_password,
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onComplete(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initialize system');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/system/wizard', {
        admin_username: adminData.admin_username,
        admin_email: adminData.admin_email,
        admin_password: adminData.admin_password,
        orchestrator_name: orchData.orchestrator_name,
        orchestrator_url: orchData.orchestrator_url,
        orchestrator_api_key: orchData.orchestrator_api_key,
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onComplete(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initialize system');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="medieval-border rounded-lg p-8 max-w-2xl w-full animate-fade-in">
        <div className="text-center mb-8">
          <img src="/peon-logo.png" alt="PEON" className="h-24 mx-auto mb-4" />
          <h1 className="warcraft-title text-4xl mb-2">Welcome to PEON</h1>
          <p className="warcraft-subtitle text-lg">Game Server Management</p>
          <p className="text-gray-400 mt-4">
            {step === 1 ? 'Create your admin account to begin' : 'Add your first orchestrator (optional)'}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8 gap-4">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'opacity-100' : 'opacity-50'}`}>
            <div className={`step-badge w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 1 ? 'step-badge--active' : ''}`}>
              {step > 1 ? <CheckCircle className="w-4 h-4" /> : '1'}
            </div>
            <span className="text-sm">Admin Account</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-600"></div>
          <div className={`flex items-center gap-2 ${step >= 2 ? 'opacity-100' : 'opacity-50'}`}>
            <div className={`step-badge w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 2 ? 'step-badge--active' : ''}`}>
              2
            </div>
            <span className="text-sm">Orchestrator</span>
          </div>
        </div>

        {step === 1 && (
          <form onSubmit={handleAdminSubmit} className="space-y-6">
            <div className="stone-texture p-6 rounded-lg">
              <h2 className="warcraft-subtitle text-2xl mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6" /> System Administrator
              </h2>
              <p className="text-sm text-gray-400 mb-4">
                This account will have full control over the system.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Username</label>
                  <input
                    type="text"
                    placeholder="Enter username"
                    value={adminData.admin_username}
                    onChange={(e) => setAdminData({ ...adminData, admin_username: e.target.value })}
                    required
                    className="w-full"
                    data-testid="wizard-username"
                    minLength={3}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Email Address</label>
                  <input
                    type="email"
                    placeholder="admin@example.com"
                    value={adminData.admin_email}
                    onChange={(e) => setAdminData({ ...adminData, admin_email: e.target.value })}
                    required
                    className="w-full"
                    data-testid="wizard-email"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Password</label>
                  <input
                    type="password"
                    placeholder="At least 8 characters"
                    value={adminData.admin_password}
                    onChange={(e) => setAdminData({ ...adminData, admin_password: e.target.value })}
                    required
                    className="w-full"
                    data-testid="wizard-password"
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Confirm Password</label>
                  <input
                    type="password"
                    placeholder="Re-enter password"
                    value={adminData.admin_password_confirm}
                    onChange={(e) => setAdminData({ ...adminData, admin_password_confirm: e.target.value })}
                    required
                    className="w-full"
                    data-testid="wizard-password-confirm"
                    minLength={8}
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-900/50 border-2 border-red-500 p-4 rounded flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-200">{error}</p>
              </div>
            )}

            <button
              type="submit"
              className="gold-button w-full py-3 rounded text-lg"
              data-testid="wizard-step1-next"
            >
              Continue to Setup
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleCompleteSetup} className="space-y-6">
            <div className="stone-texture p-6 rounded-lg">
              <h2 className="warcraft-subtitle text-2xl mb-4 flex items-center gap-2">
                <Server className="w-6 h-6" /> First Orchestrator
              </h2>
              <p className="text-sm text-gray-400 mb-4">
                Connect to a PEON orchestrator to manage game servers.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Orchestrator Name</label>
                  <input
                    type="text"
                    placeholder="e.g., Main Server"
                    value={orchData.orchestrator_name}
                    onChange={(e) => setOrchData({ ...orchData, orchestrator_name: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Base URL</label>
                  <input
                    type="text"
                    placeholder="http://server.example.com:5000"
                    value={orchData.orchestrator_url}
                    onChange={(e) => setOrchData({ ...orchData, orchestrator_url: e.target.value })}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Include the full URL with protocol and port (e.g., http://server.example.com:5000)</p>
                  <p className="text-xs text-amber-400 mt-1">If WebUI runs in Docker and you enter localhost, backend calls may be rewritten to host.docker.internal.</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">API Key</label>
                  <input
                    type="text"
                    placeholder="Enter API key"
                    value={orchData.orchestrator_api_key}
                    onChange={(e) => setOrchData({ ...orchData, orchestrator_api_key: e.target.value })}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-900/50 border-2 border-red-500 p-4 rounded flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-200">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSkipOrchestrator}
                disabled={loading}
                className="gray-button flex-1 py-3 rounded"
              >
                Skip for Now
              </button>
              <button
                type="submit"
                disabled={loading || !orchData.orchestrator_name || !orchData.orchestrator_url || !orchData.orchestrator_api_key}
                className="gold-button flex-1 py-3 rounded"
              >
                {loading ? 'Setting up...' : 'Complete Setup'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Login Component (no default credentials)
const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.classList.add('dark-mode', 'default-theme');
    document.body.classList.remove('light-mode', 'horde-theme', 'alliance-theme', 'clean-theme');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/login', { username, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="medieval-border rounded-lg p-8 max-w-md w-full animate-fade-in">
        <div className="text-center mb-8">
          <img src="/peon-logo.png" alt="PEON" className="h-20 mx-auto mb-4" />
          <h1 className="warcraft-title text-3xl mb-2">PEON</h1>
          <p className="text-gray-400">Game Server Management</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Username</label>
            <input
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full"
              data-testid="login-username"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Password</label>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full"
              data-testid="login-password"
            />
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-500 p-3 rounded flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-400" />
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="gold-button w-full py-3 rounded"
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Discord Link Modal — self-service one-time code
const DiscordLinkModal = ({ user, onClose }) => {
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generateCode = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/discord/link-code', { ttl_minutes: 15 });
      setCode(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center p-4" onClick={onClose} data-testid="discord-link-modal">
      <div
        className="medieval-border rounded-lg p-6 max-w-md w-full animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="warcraft-subtitle text-xl flex items-center gap-2">
            <MessageCircle className="w-5 h-5" /> Link your Discord
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          Connect your <span className="text-gray-200">{user?.username}</span> account to
          Discord. The code you see here is one-time and expires in 15 minutes.
        </p>

        {error && (
          <div className="bg-red-900/50 border border-red-500 p-3 rounded flex items-center gap-2 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        {code ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Your link code</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={code.code}
                  className="flex-1 font-mono text-lg text-center tracking-widest bg-black/40 border border-gray-600 rounded px-3 py-2"
                  data-testid="discord-link-code"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(code.code);
                  }}
                  className="gray-button px-3 py-2 rounded text-sm"
                  title="Copy code"
                >
                  Copy
                </button>
              </div>
            </div>
            <div>
              <ol className="text-sm text-gray-300 space-y-2">
                <li>
                  <span className="font-semibold text-gray-100">1.</span> Open Discord in the
                  PEON server.
                </li>
                <li>
                  <span className="font-semibold text-gray-100">2.</span> Run{' '}
                  <code className="bg-black/40 px-1.5 py-0.5 rounded text-indigo-300">
                    /link {code.code}
                  </code>
                </li>
                <li>
                  <span className="font-semibold text-gray-100">3.</span> Discord will confirm
                  your account is linked to <b>{user?.username}</b>.
                </li>
              </ol>
            </div>
            <div className="flex gap-3">
              <button
                onClick={generateCode}
                disabled={loading}
                className="gray-button flex-1 py-2 rounded"
              >
                {loading ? 'Generating…' : 'New code'}
              </button>
              <button onClick={onClose} className="gold-button flex-1 py-2 rounded">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-3">
            <button
              onClick={generateCode}
              disabled={loading}
              className="gold-button flex-1 py-2 rounded"
            >
              {loading ? 'Generating…' : 'Generate code'}
            </button>
            <button onClick={onClose} className="gray-button flex-1 py-2 rounded">
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ user, onLogout, pendingAlerts, onAckAlert, alertLoading }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [adminSubTab, setAdminSubTab] = useState('overview');
  const normalizeThemeMode = (value) => {
    if (value === 'classic') return 'horde';
    if (value === 'clean') return 'default';
    return ['default', 'horde', 'alliance'].includes(value) ? value : 'default';
  };
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('peon.darkMode') !== 'false');
  const [themeMode, setThemeMode] = useState(() => normalizeThemeMode(localStorage.getItem('peon.themeMode')));
  const [orchestrators, setOrchestrators] = useState([]);
  const [selectedOrch, setSelectedOrch] = useState(null);
  const [serversData, setServersData] = useState({});
  const [permissions, setPermissions] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [serverFilter, setServerFilter] = useState(null); // For filtering from home
  const [features, setFeatures] = useState({});
  const [showDiscordLink, setShowDiscordLink] = useState(false);

  const loadOrchestrators = useCallback(async () => {
    try {
      const response = await api.get('/orchestrators');
      setOrchestrators(response.data);
      if (response.data.length > 0 && !selectedOrch) {
        setSelectedOrch(response.data[0].id);
      }
    } catch (err) {
      console.error('Failed to load orchestrators:', err);
    }
  }, [selectedOrch]);

  const loadPermissions = useCallback(async () => {
    try {
      const response = await api.get('/auth/permissions');
      setPermissions(response.data.permissions);
    } catch (err) {
      console.error('Failed to load permissions:', err);
    }
  }, []);

  const loadFeatures = useCallback(async () => {
    try {
      const response = await api.get('/system/features');
      setFeatures(response.data);
    } catch (err) {
      console.error('Failed to load features:', err);
    }
  }, []);

  useEffect(() => {
    // Initial data loading
    const initializeData = async () => {
      await Promise.all([
        loadOrchestrators(),
        loadPermissions(),
        loadFeatures()
      ]);
    };
    initializeData();

    // Token refresh interval
    const refreshInterval = setInterval(async () => {
      try {
        const response = await api.post('/auth/refresh');
        localStorage.setItem('token', response.data.access_token);
      } catch (err) {
        console.error('Token refresh failed:', err);
      }
    }, 20 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [loadOrchestrators, loadPermissions, loadFeatures]);

  // Load servers data
  useEffect(() => {
    if (orchestrators.length === 0) return;

    const loadServers = async () => {
      const newData = {};
      for (const orch of orchestrators) {
        try {
          const response = await api.get(`/proxy/${orch.id}/servers`);
          newData[orch.id] = { servers: response.data.servers, last_synced: response.data.last_synced };
        } catch (err) {
          newData[orch.id] = { error: err.message };
        }
      }
      setServersData(newData);
    };

    loadServers();
    const interval = setInterval(loadServers, 60000);
    return () => clearInterval(interval);
  }, [orchestrators]);

  // Theme toggle and mode
  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    document.body.classList.toggle('light-mode', !darkMode);
    document.body.classList.toggle('default-theme', themeMode === 'default');
    document.body.classList.remove('clean-theme');
    document.body.classList.toggle('horde-theme', themeMode === 'horde');
    document.body.classList.toggle('alliance-theme', themeMode === 'alliance');
    localStorage.setItem('peon.darkMode', darkMode ? 'true' : 'false');
    localStorage.setItem('peon.themeMode', themeMode);
  }, [darkMode, themeMode]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
  };

  const dismissAlert = (id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  };

  // Handle pending system alerts (admin only)
  useEffect(() => {
    if (user.role === 'admin' && pendingAlerts && pendingAlerts.length > 0) {
      pendingAlerts.forEach(alert => {
        // Create toast-style alert
        const alertId = `sys-alert-${alert.id}`;
        setAlerts(prev => {
          if (prev.find(a => a.id === alertId)) return prev;
          return [...prev, {
            id: alertId,
            type: 'warning',
            title: 'System Alert',
            message: alert.message,
            detail: alert.detail,
            systemAlertId: alert.id
          }];
        });
      });
    }
  }, [pendingAlerts, user.role]);

  const ackSystemAlert = (systemAlertId) => {
    if (systemAlertId) {
      onAckAlert(systemAlertId);
    }
  };

  // Handle server click from home (navigate to filtered servers)
  const handleServerClick = (serverUid, orchId) => {
    setServerFilter({ serverUid, orchId });
    setActiveTab('servers');
  };

  return (
    <div className="min-h-screen p-4 md:p-6">
      <ToastContainer 
        alerts={alerts} 
        onDismiss={(id) => {
          const alert = alerts.find(a => a.id === id);
          if (alert?.systemAlertId) {
            ackSystemAlert(alert.systemAlertId);
          }
          dismissAlert(id);
        }}
      />

      {showDiscordLink && (
        <DiscordLinkModal user={user} onClose={() => setShowDiscordLink(false)} />
      )}

      {/* Header */}
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <img src="/peon-logo.png" alt="PEON" className="h-12 w-auto" />
          <div>
            <h1 className="warcraft-title text-2xl md:text-3xl">PEON</h1>
            <p className="text-xs text-gray-400">Game Server Management</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold">{user.username}</p>
            <p className="text-xs text-gray-400 capitalize">{user.role}</p>
          </div>

          <button
            onClick={() => setShowDiscordLink(true)}
            className="gray-button p-2 rounded"
            title="Link your Discord account"
            data-testid="link-discord-btn"
          >
            <MessageCircle className="w-5 h-5" />
          </button>

          <button
            onClick={handleLogout}
            className="gray-button p-2 rounded"
            title="Logout"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => { setActiveTab('home'); setServerFilter(null); }}
          className={`px-4 py-2 rounded flex items-center gap-2 transition-all ${
            activeTab === 'home' ? 'gold-button' : 'gray-button'
          }`}
          data-testid="home-tab"
        >
          <Home className="w-4 h-4" /> Home
        </button>

        {permissions?.can_view_servers && (
          <button
            onClick={() => { setActiveTab('servers'); setServerFilter(null); }}
            className={`px-4 py-2 rounded flex items-center gap-2 transition-all ${
              activeTab === 'servers' ? 'gold-button' : 'gray-button'
            }`}
            data-testid="servers-tab"
          >
            <Server className="w-4 h-4" /> Servers
          </button>
        )}

        {(permissions?.can_view_servers || user.role === 'admin') && (
          <button
            onClick={() => setActiveTab('groups')}
            className={`px-4 py-2 rounded flex items-center gap-2 transition-all ${
              activeTab === 'groups' ? 'gold-button' : 'gray-button'
            }`}
            data-testid="groups-tab"
          >
            <Users className="w-4 h-4" /> Groups
          </button>
        )}

        {permissions?.can_manage_users && (
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded flex items-center gap-2 transition-all ${
              activeTab === 'users' ? 'gold-button' : 'gray-button'
            }`}
            data-testid="users-tab"
          >
            <Users className="w-4 h-4" /> Users
          </button>
        )}

        {user.role === 'admin' && (
          <button
            onClick={() => setActiveTab('admin')}
            className={`px-4 py-2 rounded flex items-center gap-2 transition-all ${
              activeTab === 'admin' ? 'gold-button' : 'gray-button'
            }`}
            data-testid="admin-tab"
          >
            <Shield className="w-4 h-4" /> Admin
          </button>
        )}
      </nav>

      {/* Content Area */}
      <main className="medieval-border rounded-lg p-6 animate-fade-in">
        {activeTab === 'home' && (
          <HomePage
            user={user}
            orchestrators={orchestrators}
            selectedOrch={selectedOrch}
            serversData={serversData}
            permissions={permissions}
            features={features}
            onServerClick={handleServerClick}
          />
        )}

        {activeTab === 'servers' && permissions?.can_view_servers && (
          <ServersPage
            orchestrators={orchestrators}
            onOrchestratorsChange={loadOrchestrators}
            permissions={permissions}
            initialFilter={serverFilter}
          />
        )}

        {activeTab === 'groups' && (
          <GroupsPage />
        )}

        {activeTab === 'users' && permissions?.can_manage_users && (
          <UsersPage orchestrators={orchestrators} currentUser={user} />
        )}

        {activeTab === 'admin' && user.role === 'admin' && (
          <div className="space-y-6">
            <h2 className="warcraft-subtitle text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6" /> Admin Panel
            </h2>

            {/* Admin Sub-tabs */}
            <div className="flex flex-wrap gap-2 border-b border-slate-700/50 pb-4">
              {['overview', 'features', 'backup', 'notifications', 'audit'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setAdminSubTab(tab)}
                  className={`px-4 py-2 rounded text-sm transition-all ${
                    adminSubTab === tab ? 'gold-button' : 'gray-button'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {adminSubTab === 'overview' && (
              <div className="space-y-4">
                <ThemeSettingsPanel
                  darkMode={darkMode}
                  themeMode={themeMode}
                  onDarkModeChange={setDarkMode}
                  onThemeModeChange={setThemeMode}
                />

                <a
                  href={`${BACKEND_URL}/docs`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gold-button px-6 py-3 rounded inline-flex items-center gap-2"
                >
                  <Server className="w-5 h-5" /> WebUI API Documentation
                </a>
                {selectedOrch && (
                  <a
                    href={`/api/proxy/${selectedOrch}/docs?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gray-button px-6 py-3 rounded inline-flex items-center gap-2"
                  >
                    <Server className="w-5 h-5" /> Selected Orchestrator Swagger
                  </a>
                )}
              </div>
            )}

            {adminSubTab === 'features' && <FeatureTogglesPanel onFeaturesChange={loadFeatures} />}
            {adminSubTab === 'backup' && <BackupRestorePanel />}
            {adminSubTab === 'notifications' && <NotificationsPanel />}
            {adminSubTab === 'audit' && <AuditLogPanel />}
          </div>
        )}
      </main>
    </div>
  );
};

// Main App
function App() {
  const [systemStatus, setSystemStatus] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [alertLoading, setAlertLoading] = useState(false);

  useEffect(() => {
    checkSystemStatus();
  }, []);

  const checkSystemStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        const loadedUser = JSON.parse(savedUser);
        setUser(loadedUser);
        // Load pending alerts for admins
        if (loadedUser.role === 'admin') {
          loadPendingAlerts();
        }
        setLoading(false);
        return;
      }

      const response = await api.get('/system/status');
      setSystemStatus(response.data);
    } catch (err) {
      console.error('Failed to check system status:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingAlerts = async () => {
    setAlertLoading(true);
    try {
      const response = await api.get('/system/pending-alerts');
      const alerts = response.data;
      setPendingAlerts(alerts);
    } catch (err) {
      console.error('Failed to load pending alerts:', err);
    } finally {
      setAlertLoading(false);
    }
  };

  const ackAlert = async (alertId) => {
    try {
      await api.post(`/system/pending-alerts/${alertId}/ack`);
      setPendingAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img src="/peon-logo.png" alt="PEON" className="h-24 mx-auto mb-4 animate-pulse" />
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="warcraft-subtitle text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return (
      <GroupContextProvider>
        <Dashboard 
          user={user} 
          onLogout={() => setUser(null)}
          pendingAlerts={pendingAlerts}
          onAckAlert={ackAlert}
          alertLoading={alertLoading}
        />
      </GroupContextProvider>
    );
  }

  if (systemStatus && !systemStatus.has_admin) {
    return <AdminWizard onComplete={(user) => setUser(user)} />;
  }

  return <Login onLogin={(user) => setUser(user)} />;
}

export default App;
